/**
 * `<NewKeyDisplayDialog>` — shown ONCE after API key creation.
 *
 * Per FD `business-rules.md § api key endpoints` — the raw token is in
 * the response exactly once. Closing this dialog drops the token from
 * React state (the parent controls `keyRecord`); the token is never
 * recoverable from the dashboard after that.
 *
 * The Copy button uses the Clipboard API and shows a toast on success.
 */

import type { ApiKeyWithToken } from "@cometchat-ai/auth/client";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { Check, Copy, TriangleAlert } from "./icons";
import { useTranslation } from "react-i18next";

export interface NewKeyDisplayDialogProps {
  keyRecord: ApiKeyWithToken | null;
  onClose: () => void;
}

export function NewKeyDisplayDialog({ keyRecord, onClose }: NewKeyDisplayDialogProps) {
  const { t } = useTranslation("apikeys");

  async function handleCopy() {
    if (!keyRecord) return;
    try {
      await navigator.clipboard.writeText(keyRecord.token);
      toast.success(t("newKeyDialog.copiedToast"));
    } catch {
      // Clipboard access may be denied in a sandbox; fail silently — user
      // can still select the text manually below the button.
    }
  }

  return (
    <Dialog open={keyRecord !== null} onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent
        className="max-w-md gap-0 rounded-3xl p-0 sm:rounded-3xl"
        data-testid="new-key-display-dialog"
      >
        <DialogHeader className="space-y-1 px-5 pb-4 pt-5 text-left">
          <DialogTitle className="text-base font-medium tracking-[0.01em]">
            {t("newKeyDialog.title")}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-muted-foreground">
            {keyRecord?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="min-w-0 space-y-3 px-5 pb-2">
          <div
            className="flex items-start gap-2 rounded-lg px-3 py-2.5 text-[13px]"
            style={{
              color: "var(--color-danger)",
              background: "color-mix(in oklch, var(--color-danger) 10%, transparent)",
            }}
          >
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>{t("newKeyDialog.warning")}</span>
          </div>

          <div className="flex min-w-0 items-stretch gap-2">
            <code
              className="min-w-0 flex-1 break-all rounded-lg bg-[color:color-mix(in_oklch,var(--color-fg)_6%,transparent)] px-3 py-2 font-mono text-xs leading-relaxed text-foreground"
              data-testid="new-key-display-token"
            >
              {keyRecord?.token ?? ""}
            </code>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopy}
              aria-label={t("newKeyDialog.copyButton")}
              className="h-auto shrink-0 gap-1.5 rounded-md text-[13px] hover:bg-muted"
              data-testid="new-key-display-copy"
            >
              <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              {t("newKeyDialog.copyButton")}
            </Button>
          </div>
        </div>

        <DialogFooter className="px-5 pb-5 pt-3">
          <Button
            type="button"
            onClick={onClose}
            className="h-8 w-full gap-1.5 rounded-md text-[13px]"
            data-testid="new-key-display-close"
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            {t("newKeyDialog.close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
